import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { selectIndicatorStyles, selectTheme } from 'src/helpers/selectTheme'
import PropertyTypeIcon from 'src/components/elements/property-type-icon/PropertyTypeIcon'
import { CreateRequestModal } from 'src/components/elements/modals'
import { Button } from 'src/components/ui'
import { useGetCitiesQuery } from 'src/redux/api'
import { City } from 'src/types/map'

// Hero search on the home page — the fastest path into the map storefront.
// Submitting just navigates to /map with the chosen filters in the URL; the
// map rehydrates them itself (19.5), so no filter logic is duplicated here.
const HeroSearch: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: citiesData, isLoading: citiesLoading } = useGetCitiesQuery()
  const [openRequestModal, setOpenRequestModal] = useState(false)
  const [listingType, setListingType] = useState<string | undefined>(undefined)
  const [propertyType, setPropertyType] = useState<string | undefined>(undefined)
  const [city, setCity] = useState<City | null>(null)
  const [priceFrom, setPriceFrom] = useState('')
  const [priceTo, setPriceTo] = useState('')

  const listingTypeOptions = [
    { value: 'SALE', label: t('step1.sale') },
    { value: 'RENT_LONG_TERM', label: t('step1.rent-long-term') },
    { value: 'RENT_DAILY', label: t('step1.rent-daily') },
    { value: 'RENT_HOURLY', label: t('step1.rent-hourly') },
  ]
  const propertyTypeOptions = [
    { value: 'APARTMENT', label: t('step1.apartment') },
    { value: 'HOUSE', label: t('step1.house') },
    { value: 'TOWNHOUSE', label: t('step1.townhouse') },
    { value: 'DUPLEX', label: t('step1.duplex') },
    { value: 'PART_OF_HOUSE', label: t('step1.partOfHouse') },
    { value: 'ROOM', label: t('step1.room') },
    { value: 'COMMERCIAL_PROPERTY', label: t('step1.commercialProperty') },
    { value: 'PLOT', label: t('step1.plot') },
    { value: 'GARAGE', label: t('step1.garage') },
  ]
  const cityOptions =
    citiesData?.cities.map((c) => ({ value: c, label: `${c.name} (${c.count})` })) || []

  const selectMessages = {
    noOptionsMessage: () => t('select_nothing_found'),
    loadingMessage: () => t('select_loading'),
  }
  // 44px controls: comfortable touch targets on phones, consistent with the
  // h-11 buttons used across the chrome. The menu is lifted above the page
  // sections below the hero (the banner carousel paints at z-index 1 and was
  // cutting the open list off at the banner edge).
  const selectStyles = {
    control: (base: object) => ({ ...base, minHeight: '44px' }),
    menu: (base: object) => ({ ...base, zIndex: 30 }),
    ...selectIndicatorStyles,
  }

  const mapLink = (patch: Record<string, string | undefined>) => {
    const params = new URLSearchParams()
    Object.entries(patch).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })
    const qs = params.toString()
    return qs ? `/map?${qs}` : '/map'
  }

  const handleFind = () =>
    navigate(
      mapLink({
        city: city?.placeId,
        listingType,
        propertyType,
        priceFrom: priceFrom || undefined,
        priceTo: priceTo || undefined,
      }),
    )

  const chips = [
    { label: t('step1.apartment'), to: mapLink({ propertyType: 'APARTMENT' }), type: 'APARTMENT' },
    { label: t('step1.house'), to: mapLink({ propertyType: 'HOUSE' }), type: 'HOUSE' },
    { label: t('step1.plot'), to: mapLink({ propertyType: 'PLOT' }), type: 'PLOT' },
    { label: t('step1.garage'), to: mapLink({ propertyType: 'GARAGE' }), type: 'GARAGE' },
    {
      label: t('step1.commercialProperty'),
      to: mapLink({ propertyType: 'COMMERCIAL_PROPERTY' }),
      type: 'COMMERCIAL_PROPERTY',
    },
    { label: t('step1.sale'), to: mapLink({ listingType: 'SALE' }) },
    { label: t('step1.rent-long-term'), to: mapLink({ listingType: 'RENT_LONG_TERM' }) },
  ]

  return (
    <section className="flex flex-col gap-4 p-5 md:p-7 rounded-2xl bg-inverse text-white">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-[1.375rem] md:text-3xl font-black leading-tight">
          {t('home_hero.title')}
        </h2>
        <p className="text-sm md:text-base text-white/80">{t('tagline')}</p>
      </div>
      {/* Phones: one field that opens the map with its filters — the five
          fields used to fill the whole first screen, no listing in sight */}
      <button
        type="button"
        onClick={() => navigate('/map', { state: { openFilters: true } })}
        className="md:hidden flex items-center gap-2.5 h-[3.25rem] px-4 rounded-[0.875rem] bg-surface text-ink-2 text-[0.9375rem] font-semibold text-left"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="stroke-ink"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="6" />
          <path d="M20 20l-4.5-4.5" />
        </svg>
        {t('home_hero.search_field')}
      </button>
      <div className="hidden md:flex md:flex-wrap md:items-center gap-2 p-3 rounded-xl bg-surface">
        <Select
          {...selectMessages}
          aria-label={t('step1.listingType')}
          className="w-full md:flex-1 md:min-w-[11rem] text-ink"
          placeholder={t('step1.listingType')}
          options={listingTypeOptions}
          value={listingTypeOptions.find((o) => o.value === listingType) || null}
          onChange={(option) => setListingType(option?.value || undefined)}
          isClearable
          styles={selectStyles}
          theme={selectTheme}
        />
        <Select
          {...selectMessages}
          aria-label={t('property_type')}
          className="w-full md:flex-1 md:min-w-[11rem] text-ink"
          placeholder={t('property_type')}
          options={propertyTypeOptions}
          value={propertyTypeOptions.find((o) => o.value === propertyType) || null}
          onChange={(option) => setPropertyType(option?.value || undefined)}
          isClearable
          styles={selectStyles}
          theme={selectTheme}
        />
        <Select
          {...selectMessages}
          aria-label={t('MapPage.select-city')}
          className="w-full md:flex-1 md:min-w-[11rem] text-ink"
          placeholder={t('MapPage.select-city')}
          options={cityOptions}
          value={cityOptions.find((o) => o.value.placeId === city?.placeId) || null}
          onChange={(option) => setCity(option?.value || null)}
          isLoading={citiesLoading}
          isClearable
          styles={selectStyles}
          theme={selectTheme}
        />
        <input
          type="number"
          min={0}
          aria-label={`${t('price')} (${t('from')})`}
          className="w-full md:w-28 h-11 px-3 rounded-xl border border-solid border-ink/[0.18] text-sm bg-surface text-ink"
          placeholder={`${t('price')} (${t('from')})`}
          value={priceFrom}
          onChange={(e) => setPriceFrom(e.target.value)}
        />
        <input
          type="number"
          min={0}
          aria-label={`${t('price')} (${t('to')})`}
          className="w-full md:w-28 h-11 px-3 rounded-xl border border-solid border-ink/[0.18] text-sm bg-surface text-ink"
          placeholder={`${t('price')} (${t('to')})`}
          value={priceTo}
          onChange={(e) => setPriceTo(e.target.value)}
        />
        {/* One button: "Відкрити на карті" next to it did exactly the same */}
        <Button className="w-full md:w-auto shrink-0" onClick={handleFind}>
          <span>{t('home_hero.find')}</span>
        </Button>
      </div>
      <div className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-1 md:flex-wrap md:overflow-visible md:mx-0 md:px-0 md:pb-0">
        {chips.map((chip) => (
          <Link
            key={chip.to}
            to={chip.to}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 h-10 md:h-9 rounded-full bg-white/10 hover:bg-white/20 text-sm font-bold transition-colors duration-200"
          >
            {chip.type && <PropertyTypeIcon type={chip.type} className="w-4 h-4 fill-white" />}
            {chip.label}
          </Link>
        ))}
      </div>
      {/* On phones the header buttons are hidden, so the cabinet carries its
          own way to create a запит (39.4). Two rows on a 320px phone, where
          "Сформувати запит" ran out of the card. */}
      <div className="flex flex-wrap gap-2.5 md:hidden">
        <Button className="flex-1 !text-[0.9375rem] !font-extrabold" size="lg" onClick={handleFind}>
          {t('home_hero.find')}
        </Button>
        <button
          type="button"
          onClick={() => setOpenRequestModal(true)}
          className="flex-1 inline-flex items-center justify-center h-12 px-3 rounded-xl border border-solid border-white/50 text-[0.9375rem] font-extrabold whitespace-nowrap hover:bg-white/10 transition-colors duration-200"
        >
          {t('create_a_request')}
        </button>
      </div>
      <CreateRequestModal open={openRequestModal} handleClose={() => setOpenRequestModal(false)} />
    </section>
  )
}

export default HeroSearch
